import { ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { Button } from '@/shared/components/shadcn/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/components/shadcn/collapsible';
import { cn } from '@/shared/utils/cn';

export const PostSectionTitle = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex items-center">
      <span className="text-xl font-semibold">{children}</span>
    </div>
  );
};

export const KeyValuePair = ({ label, value }: { label: ReactNode; value: string }) => {
  return (
    <div className="flex items-start gap-1.5">
      <span className="font-regular leading-4 font-semibold">{label}</span>
      <span className="text-muted-foreground flex-1 text-base leading-4 font-medium text-ellipsis">
        {value}
      </span>
    </div>
  );
};

export const OutlineBox = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => {
  return <div className={cn('rounded-lg border p-6', className)}>{children}</div>;
};

export const MutedBox = ({ children, className }: { children: ReactNode; className?: string }) => {
  return <div className={cn('bg-muted rounded-lg border p-6', className)}>{children}</div>;
};

export const CollapsibleBox = ({
  children,
  trigger,
  initialOpen = false,
  actionNode,
}: {
  children: ReactNode;
  trigger: ReactNode;
  initialOpen?: boolean;
  actionNode?: ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="flex items-center justify-between">
        <CollapsibleTrigger>{trigger}</CollapsibleTrigger>
        <div className="flex items-center gap-2">
          {actionNode}
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="icon">
              <ChevronDown
                className={cn(
                  'size-4 cursor-pointer transition-all duration-200',
                  isOpen && 'rotate-180',
                )}
              />
            </Button>
          </CollapsibleTrigger>
        </div>
      </div>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  );
};
